import { useQuery } from '@tanstack/react-query';
import api from '../api/client.js';
import clsx from 'clsx';

/**
 * Customer 360 / Account Scorecard.
 *
 * Top-line "over-under" answer for an account: are we ahead or behind on
 * sales for the trailing 12 months vs the prior 12 months, with GP and GP %,
 * the last invoice and the open order book. Data: ASE's D3 invoices.
 *
 * Renders nothing if the scorecard endpoint errors (e.g. sales views are not
 * enabled for this user).
 */
export default function CustomerScorecard({ customerId }) {
  const q = useQuery({
    queryKey: ['customer-scorecard', customerId],
    queryFn: () => api.get(`/customers/${customerId}/scorecard`).then(r => r.data.data),
    retry: false,
  });

  if (q.isLoading) return <div className="text-muted text-sm">Loading scorecard…</div>;
  if (q.isError) return null;
  const s = q.data;
  if (!s) return null;

  const lastInvoiceDays = s.activity.last_invoice_at
    ? Math.floor((Date.now() - new Date(s.activity.last_invoice_at).getTime()) / 86400000)
    : null;

  return (
    <section className="card p-4 space-y-4">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="section-eyebrow">Account scorecard</div>
          <Verdict verdict={s.verdict} delta={s.sales} />
        </div>
        <div className="text-xs text-muted">last 12 months vs prior 12 months</div>
      </div>
      <div className="rule-crimson" />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MoneyCard label="Sales" r={s.sales} bold />
        <MoneyCard label="GP" r={s.gp} />
        <div className="bg-panel rounded p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted">GP % (last 12mo)</div>
          <div className="text-xl font-semibold">{s.gp_pct.t12 == null ? '—' : `${s.gp_pct.t12.toFixed(1)}%`}</div>
          <div className="text-xs mt-1">
            prev: <span className="text-muted">{s.gp_pct.p12 == null ? '—' : `${s.gp_pct.p12.toFixed(1)}%`}</span>
          </div>
        </div>
        <div className="bg-panel rounded p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted">This month</div>
          <div className="text-xl font-semibold">{fmtMoney(s.mtd.sales)}</div>
          <div className="text-xs mt-1">GP <span className="text-muted">{fmtMoney(s.mtd.gp)}</span></div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="bg-panel rounded p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted">Invoices</div>
          <div className="text-xl font-semibold">{s.activity.invoices_t12} in last 12mo</div>
          <div className="text-xs mt-2 space-y-0.5">
            <div>prev 12mo: <span className="text-muted">{s.activity.invoices_p12}</span></div>
            <div className={clsx(lastInvoiceDays != null && lastInvoiceDays > 90 && 'text-crimson')}>
              last invoice: {lastInvoiceDays == null ? 'none on file'
                : `${new Date(s.activity.last_invoice_at).toLocaleDateString()} (${lastInvoiceDays}d ago)`}
            </div>
          </div>
        </div>
        <div className="bg-panel rounded p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted">Open orders</div>
          <div className="text-xl font-semibold">{fmtMoney(s.open_orders.value)}</div>
          <div className="text-xs mt-2 space-y-0.5">
            <div>
              {s.open_orders.orders} order{s.open_orders.orders === 1 ? '' : 's'} · {s.open_orders.lines} line{s.open_orders.lines === 1 ? '' : 's'}
            </div>
            {s.open_orders.backordered_lines > 0 && (
              <div className="text-crimson">{s.open_orders.backordered_lines} line{s.open_orders.backordered_lines === 1 ? '' : 's'} backordered</div>
            )}
            {s.open_orders.as_of && (
              <div className="text-muted">as of {new Date(s.open_orders.as_of).toLocaleDateString()}</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Verdict({ verdict, delta }) {
  const map = {
    over:   { label: 'AHEAD',   color: 'text-emerald-700', sign: '+' },
    under:  { label: 'BEHIND',  color: 'text-crimson',     sign: '-' },
    flat:   { label: 'FLAT',    color: 'text-navy',        sign: ''  },
    new:    { label: 'NEW',     color: 'text-emerald-700', sign: '+' },
    silent: { label: 'SILENT',  color: 'text-crimson',     sign: ''  },
  };
  const v = map[verdict] || map.flat;
  const abs = Math.abs(delta.abs_delta);
  const pct = delta.pct_delta;
  return (
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className={clsx('text-2xl font-bold', v.color)}>{v.label}</span>
      {(verdict === 'over' || verdict === 'under') && (
        <>
          <span className={clsx('text-xl font-semibold', v.color)}>
            {v.sign}{fmtMoney(abs)}
          </span>
          {pct != null && (
            <span className={clsx('text-sm', v.color)}>
              ({pct > 0 ? '+' : ''}{pct.toFixed(0)}%)
            </span>
          )}
          <span className="text-sm text-muted">in sales</span>
        </>
      )}
      {verdict === 'new' && <span className="text-sm text-muted">no sales in the prior 12 months</span>}
      {verdict === 'silent' && <span className="text-sm text-muted">no sales in the last 12 months</span>}
    </div>
  );
}

function MoneyCard({ label, r, bold }) {
  const delta = r.abs_delta;
  const pct = r.pct_delta;
  const positive = delta > 0;
  const negative = delta < 0;
  return (
    <div className={clsx('bg-panel rounded p-3', bold && 'ring-1 ring-navy')}>
      <div className="text-[10px] uppercase tracking-wider text-muted">{label} (last 12mo)</div>
      <div className={clsx('text-xl', bold ? 'font-bold' : 'font-semibold')}>{fmtMoney(r.t12)}</div>
      <div className="text-xs mt-1">
        prev: <span className="text-muted">{fmtMoney(r.p12)}</span>
        {Math.abs(delta) > 0 && (
          <span className={clsx('ml-2 font-medium',
            positive && 'text-emerald-700', negative && 'text-crimson')}>
            {positive ? '▲' : '▼'} {fmtMoney(Math.abs(delta))}
            {pct != null && ` (${pct > 0 ? '+' : ''}${pct.toFixed(0)}%)`}
          </span>
        )}
      </div>
    </div>
  );
}

function fmtMoney(n) {
  if (n == null) return '—';
  const v = Number(n);
  const abs = Math.abs(v);
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
}
